import { Trans, useTranslation } from "react-i18next";
import { MetadataType } from "types";
import { useChartProfileSelectStatusContext } from "components/charting/contexts/ChartProfileSelectStatusContext";
import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import { useChartSetSelectStatusContext } from "components/charting/contexts/ChartSetSelectStatusContext";
import useChartSelections from "components/charting/hooks/useChartSelections";
import useProfileTypeQueryState, { ProfileType } from "components/charting/hooks/useProfileTypeQueryState";
import ChartNoData from "components/charting/ChartNoData";
import ChartLoadingFallback from "components/charting/ChartLoadingFallback";
import OneRepMaxChartNoDataVelocity from "components/charting/OneRepMaxChartV2/OneRepMaxChartNoData/components/OneRepMaxChartNoDataVelocity";
import Bold from "components/primitives/Bold";

interface AthleteProfilingNoDataWrapperProps {
  children: React.ReactNode;
}

function AthleteProfilingNoDataWrapper(props: AthleteProfilingNoDataWrapperProps) {
  const { t } = useTranslation();
  const selections = useChartSelections();
  const { options: setOptions } = useChartSetSelectContext();
  const setOptionsStatus = useChartSetSelectStatusContext();
  const profileOptionsStatus = useChartProfileSelectStatusContext();
  const [profileType] = useProfileTypeQueryState();

  const hasMeasurement = selections.measures[0]?.type === MetadataType.EXERCISE;
  const hasNoSets = setOptionsStatus === "success" && !setOptions.length;

  if (setOptionsStatus === "fetching" || profileOptionsStatus === "fetching") {
    return <ChartLoadingFallback>{t("athlete_profile.profiling_loading_sets")}</ChartLoadingFallback>;
  }

  if (!hasMeasurement) {
    return (
      <ChartNoData
        title={t("athlete_profile.profiling_select_measurement_title")}
        description={
          <Trans i18nKey="athlete_profile.profiling_select_measurement_description" components={{ Bold: <Bold /> }}>
            Profiles are available only for VBT exercises. Select a VBT exercise to preview and compare profiles.
          </Trans>
        }
      />
    );
  }

  /**
   * A LVP profile cannot exist without any sets.
   * So guard against rendering the profiling chart when there are no sets available
   * covers both cases where the chart would have no data due to missing sets.
   */
  if (hasNoSets) {
    if (profileType === ProfileType.Velocity) {
      return <OneRepMaxChartNoDataVelocity />;
    }

    return (
      <ChartNoData
        title={t("athlete_profile.profiling_no_sets_title")}
        description={t("athlete_profile.profiling_no_sets_description")}
      />
    );
  }

  return <>{props.children}</>;
}

export default AthleteProfilingNoDataWrapper;
